import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, Calendar, Clock, Users, Sparkles, Heart, CheckCircle, ArrowRight, Mail, Video, PlayCircle, HandHeart, ClipboardList, FileQuestion } from 'lucide-react';

/**
 * Program Preview Modal - Shows detailed info for locked programs
 * Allows users to learn more before purchasing
 */
const ProgramPreviewModal = ({ program, isOpen, onClose }) => {
  if (!isOpen || !program) return null;

  // Get the preview content based on program ID
  const previewContent = programPreviews[program.id];
  
  if (!previewContent) {
    // Fallback for programs without detailed preview
    return (
      <BasicPreviewModal program={program} onClose={onClose} />
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-forest/90 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 30 }}
          className="bg-cream rounded-3xl max-w-3xl w-full my-8 shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hero Section */}
          <div className="relative">
            <img
              src={previewContent.heroImage}
              alt={previewContent.title}
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/50 to-transparent" />
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 hover:bg-white/30 transition-colors"
            >
              <X size={24} className="text-white" />
            </button>

            {/* Badges */}
            <div className="absolute top-4 left-4 flex gap-2">
              {previewContent.badges?.map((badge, idx) => (
                <span 
                  key={idx}
                  className={`px-3 py-1.5 rounded-full text-xs font-body font-medium ${badge.color}`}
                >
                  {badge.text}
                </span>
              ))}
            </div>

            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <p className="text-white font-heading text-lg md:text-xl tracking-wide italic mb-2 drop-shadow-lg">
                {previewContent.subtitle}
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight">
                {previewContent.title}
              </h2>
              <p className="text-cream/80 font-body mt-2 flex items-center gap-2">
                <Calendar size={16} />
                {previewContent.dates}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8 space-y-8">
            {/* Opening Question */}
            {previewContent.openingQuestion && (
              <div className="text-center py-4">
                <p className="font-heading text-2xl md:text-3xl text-forest italic leading-relaxed">
                  {previewContent.openingQuestion}
                </p>
              </div>
            )}

            {/* The Context */}
            {previewContent.context && (
              <div className="bg-stone-100 rounded-2xl p-6">
                <p className="text-stone-600 font-body leading-relaxed whitespace-pre-line">
                  {previewContent.context}
                </p>
              </div>
            )}

            {/* The Promise */}
            {previewContent.promise && (
              <div className="bg-terracotta/10 rounded-2xl p-6 text-center">
                <p className="font-heading text-xl text-forest">
                  {previewContent.promise}
                </p>
              </div>
            )}

            {/* Questionnaires Section */}
            {previewContent.hasQuestionnaires && previewContent.questionnaires && (
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-6 md:p-8 border border-violet-100">
                <h3 className="font-heading text-2xl text-forest mb-3 text-center flex items-center justify-center gap-2">
                  <ClipboardList size={24} className="text-violet-500" />
                  {previewContent.questionnaires.title}
                </h3>
                <p className="text-stone-600 font-body text-center mb-6 leading-relaxed">
                  {previewContent.questionnaires.description}
                </p>
                
                <div className="space-y-4">
                  {previewContent.questionnaires.items.map((item, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white rounded-xl p-5 shadow-sm border border-stone-200"
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-full flex-shrink-0 ${idx === 0 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                          <FileQuestion size={24} className={idx === 0 ? 'text-emerald-600' : 'text-amber-600'} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-heading text-lg text-forest mb-1">{item.name}</h4>
                          <p className="text-stone-600 font-body text-sm leading-relaxed mb-3">{item.description}</p>
                          {item.comingSoon ? (
                            <button
                              disabled
                              className="bg-stone-200 text-stone-500 px-5 py-2.5 rounded-full font-body text-sm font-medium cursor-not-allowed flex items-center gap-2"
                            >
                              <Clock size={16} />
                              Coming Soon
                            </button>
                          ) : item.url?.startsWith('/') ? (
                            <Link
                              to={item.url}
                              onClick={onClose}
                              className="bg-forest text-white px-5 py-2.5 rounded-full font-body text-sm font-medium hover:bg-forest-dark transition-colors inline-flex items-center gap-2"
                            >
                              {item.buttonText}
                              <ArrowRight size={16} />
                            </Link>
                          ) : (
                            <a
                              href={item.url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-forest text-white px-5 py-2.5 rounded-full font-body text-sm font-medium hover:bg-forest-dark transition-colors inline-flex items-center gap-2"
                            >
                              {item.buttonText}
                              <ArrowRight size={16} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Personal Story */}
            {previewContent.personalStory && (
              <div className="border-l-4 border-terracotta pl-6">
                <p className="text-stone-600 font-body leading-relaxed italic">
                  {previewContent.personalStory}
                </p>
              </div>
            )}

            {/* The Commitment Section */}
            {previewContent.commitment && (
              <div className="bg-forest text-cream rounded-2xl p-6 md:p-8">
                <h3 className="font-heading text-2xl text-terracotta mb-4 flex items-center gap-2">
                  <Clock size={24} />
                  {previewContent.commitment.title}
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-cream/70 font-body text-sm uppercase tracking-wider mb-2">Live Sessions</p>
                    {previewContent.commitment.schedule.map((item, idx) => (
                      <p key={idx} className="text-cream font-body">{item}</p>
                    ))}
                  </div>
                  <div>
                    <p className="text-cream/70 font-body text-sm uppercase tracking-wider mb-2">Duration</p>
                    <p className="text-cream font-body">{previewContent.commitment.duration}</p>
                  </div>
                </div>

                {/* Weekly Structure */}
                {previewContent.commitment.weeks && (
                  <div className="bg-cream/10 rounded-xl p-4">
                    <p className="text-cream/70 font-body text-sm uppercase tracking-wider mb-3">Each Week Builds Intentionally</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {previewContent.commitment.weeks.map((week, idx) => (
                        <div key={idx} className="text-center">
                          <span className="text-terracotta font-heading text-lg">Week {idx + 1}</span>
                          <p className="text-cream font-body text-sm">{week}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Ways to Participate Section */}
            {previewContent.waysToParticipate && (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 md:p-8 border border-emerald-100">
                <h3 className="font-heading text-2xl text-forest mb-3 text-center">
                  {previewContent.waysToParticipate.title}
                </h3>
                <p className="text-stone-600 font-body text-center mb-6 leading-relaxed">
                  {previewContent.waysToParticipate.schedule}
                </p>
                
                <div className="space-y-4">
                  {previewContent.waysToParticipate.options.map((option, idx) => (
                    <div 
                      key={idx} 
                      className={`rounded-xl p-5 ${
                        idx === 0 ? 'bg-white shadow-md border-2 border-emerald-200' : 
                        idx === 1 ? 'bg-white shadow-sm border border-stone-200' : 
                        'bg-violet-100 border border-violet-200'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-full flex-shrink-0 ${
                          idx === 0 ? 'bg-emerald-100' : 
                          idx === 1 ? 'bg-amber-100' : 
                          'bg-violet-200'
                        }`}>
                          {idx === 0 && <Video size={24} className="text-emerald-600" />}
                          {idx === 1 && <PlayCircle size={24} className="text-amber-600" />}
                          {idx === 2 && <HandHeart size={24} className="text-violet-600" />}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-heading text-lg text-forest mb-1">{option.name}</h4>
                          <p className="text-stone-600 font-body text-sm leading-relaxed">{option.description}</p>
                          {option.perks && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {option.perks.map((perk, perkIdx) => (
                                <span key={perkIdx} className="bg-emerald-50 text-emerald-700 text-xs px-2 py-1 rounded-full font-body">
                                  {perk}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Investment Section */}
            {previewContent.investment && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg">
                <h3 className="font-heading text-2xl text-forest mb-6 flex items-center gap-2">
                  <Sparkles size={24} className="text-terracotta" />
                  {previewContent.investment.title}
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Early Bird */}
                  <div className="bg-emerald-50 rounded-xl p-5 border-2 border-emerald-200">
                    <p className="text-emerald-700 font-body text-sm uppercase tracking-wider mb-1">Early Bird</p>
                    <p className="font-heading text-3xl text-emerald-700">{previewContent.investment.earlyBird.price}</p>
                    <p className="text-emerald-600 font-body text-sm">{previewContent.investment.earlyBird.deadline}</p>
                  </div>
                  {/* Regular */}
                  <div className="bg-stone-100 rounded-xl p-5">
                    <p className="text-stone-500 font-body text-sm uppercase tracking-wider mb-1">Regular Price</p>
                    <p className="font-heading text-3xl text-stone-600">{previewContent.investment.regular.price}</p>
                    <p className="text-stone-500 font-body text-sm">{previewContent.investment.regular.note}</p>
                  </div>
                </div>

                {/* Includes */}
                <div>
                  <p className="text-forest font-body font-medium mb-3">Includes:</p>
                  <div className="grid md:grid-cols-2 gap-2">
                    {previewContent.investment.includes.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle size={18} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-stone-600 font-body text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Accessibility Note */}
            {previewContent.accessibility && (
              <div className="bg-violet-50 rounded-2xl p-6">
                <h4 className="font-heading text-lg text-forest mb-2 flex items-center gap-2">
                  <Heart size={20} className="text-violet-500" />
                  Accessibility
                </h4>
                <p className="text-stone-600 font-body text-sm mb-3">
                  {previewContent.accessibility.physical}
                </p>
                <p className="text-violet-700 font-body text-sm italic">
                  {previewContent.accessibility.financial}
                </p>
              </div>
            )}

            {/* Who This Is For */}
            {previewContent.whoIsItFor && (
              <div>
                <h3 className="font-heading text-xl text-forest mb-4 flex items-center gap-2">
                  <Users size={20} className="text-terracotta" />
                  Who This Is For
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {previewContent.whoIsItFor.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-stone-50 rounded-lg p-3">
                      <span className="w-2 h-2 bg-terracotta rounded-full" />
                      <span className="text-stone-600 font-body text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Closing */}
            {previewContent.closing && (
              <div className="text-center py-6 border-t border-stone-200">
                <p className="font-heading text-xl text-forest mb-2">
                  {previewContent.closing.headline}
                </p>
                <p className="text-terracotta font-body font-medium">
                  {previewContent.closing.subline}
                </p>
              </div>
            )}

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a
                href={previewContent.bookingUrl || `mailto:heyjbennett@yahoo.ca?subject=Interested in ${previewContent.title}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-terracotta text-white py-4 px-6 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors flex items-center justify-center gap-2"
              >
                <ArrowRight size={18} />
                Register Now
              </a>
              <button
                onClick={onClose}
                className="flex-1 bg-stone-200 text-forest py-4 px-6 rounded-full font-body font-medium hover:bg-stone-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Basic preview for programs without detailed content
const BasicPreviewModal = ({ program, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-forest/80 z-50 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-cream rounded-2xl max-w-lg w-full p-8"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-stone-400 hover:text-stone-600"
      >
        <X size={24} />
      </button>
      
      <h2 className="font-heading text-2xl text-forest mb-4">{program.title}</h2>
      <p className="text-stone-600 font-body mb-6">{program.description}</p>
      
      <p className="text-stone-500 font-body text-sm italic mb-6">
        More details coming soon. Contact us for more information.
      </p>
      
      <a
        href={`mailto:heyjbennett@yahoo.ca?subject=Interested in ${program.title}`}
        className="block w-full bg-terracotta text-white py-3 rounded-full font-body font-medium text-center hover:bg-terracotta-dark transition-colors"
      >
        Contact for Details
      </a>
    </motion.div>
  </motion.div>
);

// Program preview content data
const programPreviews = {
  'terrain': {
    title: 'SLOW WELLNESS',
    subtitle: 'Tapping into your Terrain',
    dates: 'Self-paced • 12-month journey',
    heroImage: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=800',
    badges: [
      { text: 'Foundation Program', color: 'bg-emerald-100 text-emerald-700' },
      { text: '12 Months', color: 'bg-violet-100 text-violet-700' }
    ],
    openingQuestion: 'What if lasting wellness isn\'t about quick fixes?\nWhat if it\'s about understanding your unique terrain?',
    context: `Real transformation takes time.

Most wellness programs give you a plan and expect you to follow it. But your body isn't generic—it has its own unique needs, patterns, and wisdom.

This year-long journey guides you through rebuilding your health from the ground up—addressing sleep, nutrition, movement, stress, and the deeper aspects of wellbeing that most programs miss.`,
    promise: 'Discover what YOUR body truly needs to thrive.',
    hasQuestionnaires: true,
    questionnaires: {
      title: 'Start with Understanding',
      description: 'Before diving into transformation, we need to understand where you are. These two assessments help us create your personalized wellness map.',
      items: [
        {
          name: 'Metabolic Rhythm Quiz',
          description: 'How your nervous system prefers to be fueled — and how stress may be shaping your hunger, cravings, and energy.',
          buttonText: 'Take Quiz',
          comingSoon: false,
          url: '/quiz/metabolic-rhythm'
        },
        {
          name: 'The Vitality Potential Tool',
          description: 'A comprehensive look at the factors that may be depleting your energy — and where your natural resilience lives.',
          buttonText: 'Take Assessment',
          comingSoon: false,
          url: '/quiz/lifestyle-assessment'
        },
        {
          name: 'Wellness Wheel',
          description: 'A daily check-in based on the 9 Pillars of Wellness. Rate each area of your life, see your balance wheel, and receive personalized guidance.',
          buttonText: 'Open Wellness Wheel',
          comingSoon: false,
          url: '/programs/terrain/wellness-wheel'
        }
      ]
    },
    whoIsItFor: [
      'Ready for deep, lasting transformation',
      'Tired of quick fixes that don\'t stick',
      'Want to understand your unique body',
      'Committed to a year of growth',
      'Looking for personalized guidance',
      'Ready to address root causes',
      'Want to build sustainable habits',
      'Seeking holistic wellbeing'
    ],
    closing: {
      headline: 'True transformation is a journey, not a destination.',
      subline: 'Give yourself the gift of a year.'
    },
    bookingUrl: null
  },
  'fascia-reset': {
    title: 'The 28 Day Reset',
    subtitle: 'A Return to Your Body\'s Intelligence',
    dates: 'May 1 – May 28, 2026',
    heroImage: 'https://customer-assets.emergentagent.com/job_greeting-hub-217/artifacts/gzscn1cb_WhatsApp%20Image%202026-02-11%20at%206.46.53%20PM.jpeg',
    badges: [
      { text: 'Starts May 1', color: 'bg-emerald-100 text-emerald-700' },
      { text: 'Early Bird Available', color: 'bg-amber-100 text-amber-700' }
    ],
    openingQuestion: 'What if your body isn\'t broken?\nWhat if it\'s simply been overridden?',
    context: `We live in a world of noise.

Environmental toxicity. Processed food. Endless scrolling. News cycles designed to dysregulate. Stress layered on stress layered on stress.

And somewhere in all of that… we stepped out of our bodies.`,
    promise: 'What would it be like to remember your body\'s intelligence?',
    personalStory: `This program is modelled after the powerful structure of the Human Garage 28-Day Reset—the framework that arrived in my life at exactly the right moment. I found it during a phase when I needed something deeper. I was stuck in pain, old stacked injuries, and my body was growing something it wasn't supposed to. This created a stuck sensation I had never experienced before.

It became the most effective self-healing practice I had ever experienced. Powerful enough that I trained and became a certified Fascial Maneuvers Coach.

Now it's time to share as I continue to grow with it myself and keep experiencing its magic.`,
    commitment: {
      title: 'The Commitment',
      schedule: [
        'M-Th 7am',
        'Fri 12:15pm',
        'Sat-Sun 8am',
        '15–30 minutes daily'
      ],
      duration: 'Short. Intentional. Powerful.',
      weeks: ['Reconnect', 'Release', 'Regulate', 'Integrate']
    },
    waysToParticipate: {
      title: 'Many Ways to Join In',
      schedule: '7am Monday–Thursday, 12:15pm Friday (longer practice), and 8am weekends — with full web program access to recordings if you can\'t attend live.',
      options: [
        {
          name: '1. Zoom Lives + Full Access',
          description: 'Join live sessions via Zoom or catch them later through the website. Full access to all the bells and whistles.',
          perks: ['1 studio class/week', '50% off Sunday Elbow Beach ($35)', '1 BR service']
        },
        {
          name: '2. Self Practice',
          description: 'Follow along on the website with self-guided practice videos. Clean, simple — 1 practice per day (or repeat if you\'re loving it) plus educational tidbits and guidance.',
          perks: null
        },
        {
          name: '3. Pay What You Can',
          description: 'We want it to feel accessible. Please reach out — info below.',
          perks: null
        }
      ]
    },
    investment: {
      title: 'Inside Track Investment',
      earlyBird: {
        price: '$222',
        deadline: 'by February 22'
      },
      regular: {
        price: '$252',
        note: 'after February 22'
      },
      includes: [
        'All live sessions',
        'App access to recordings',
        'Weekly education + insights',
        '1 Breathing Room class/week during March',
        '50% off Sunday Elbow Beach Fascial Maneuvers',
        '50% off one Breathing Room service'
      ]
    },
    accessibility: {
      physical: 'The movements are very accessible. Most are done from standing or sitting positions. The positions are simple—it\'s the way we work with fascia and breath that makes it powerful.',
      financial: 'We wish for anyone to have the opportunity to access this.\n\nOnly interested in video access? Great—no extra bells and whistles, just video access and wellness coaching tips and guidance embedded to really kick start this journey. We\'ve created a PWYC option.\n\nIf you\'re ready to shift your life, body and wellness and looking for a self-guided option, or experiencing financial constraints, please explore our Pay What You Can option. The suggestion is $49-149. You choose.\n\nContact Breathing Room for details. Wellness should not be reserved for certain income brackets. Make this program work for you. Contact breathinroombda@outlook.com or call 441-261-5915 for options.'
    },
    whoIsItFor: [
      'Chronic pain or recurring tension',
      'Old injuries and scar tissue',
      'Nervous system overload',
      'Feeling stuck or heavy',
      'Rebuilding strength and function',
      'Moving through or recovering from disease and illness',
      'Wanting deeper embodiment',
      'Anyone who wants to age backwards and get healthier',
      'Anyone ready to take healing back into their own hands',
      'If you are healthy and want to move even better and prevent future injuries'
    ],
    closing: {
      headline: 'You do not need more information. You need reconnection.',
      subline: 'May 1. 28 days. Daily practice.'
    },
    bookingUrl: 'https://www.wellnessliving.com/rs/event/breathing_room-rws2se?k_class=957840&k_class_tab=75840'
  }
};

export default ProgramPreviewModal;
